import { convertBytesToSize } from "@/utils/convertBytesToSize";
import convertToBytes from "@/utils/convertToBytes";

export type StorageLimitItem = { size?: number };

/** `hot_storage_limit_gb` as the license reports it; `null` / `undefined` means unlimited. */
export type StorageLimitGb = number | string | null | undefined;

export type EvaluateStorageLimitInput = {
  /** Tenant `hot_storage_limit_gb`; `null` / `undefined` / non-numeric means unlimited. */
  limitGb: StorageLimitGb;
  /** Current tenant usage in bytes (`GET /system/info` → `files.total_size`). */
  usedBytes: number;
  /** Bytes already accepted into the upload queue but not yet started server-side. */
  pendingBytes?: number;
  /** Bytes in the batch being checked. */
  incomingBytes: number;
};

export type EvaluateStorageLimitResult =
  | { allowed: true }
  | { allowed: false; limitBytes: number };

/** Sum `size` over a batch, treating a missing or non-finite size as 0. */
export const sumSizes = (items: StorageLimitItem[]): number =>
  items.reduce(
    (total, item) =>
      total +
      (typeof item.size === "number" && Number.isFinite(item.size)
        ? item.size
        : 0),
    0
  );

/**
 * Convert `hot_storage_limit_gb` to bytes. Returns `null` when the tenant is
 * unlimited (mirrors the server's `null` rule). `0` is a real limit.
 */
export const resolveStorageLimitBytes = (
  limitGb: StorageLimitGb
): number | null => {
  if (limitGb === null || limitGb === undefined || limitGb === "") return null;
  const gb = Number(limitGb);
  if (!Number.isFinite(gb)) return null;
  return convertToBytes(gb, "GB");
};

/**
 * Same rule as monolith-api's multipart limit check: exactly at the limit
 * passes (`used + pending + incoming <= limit`).
 */
export const evaluateStorageLimit = ({
  limitGb,
  usedBytes,
  pendingBytes = 0,
  incomingBytes,
}: EvaluateStorageLimitInput): EvaluateStorageLimitResult => {
  const limitBytes = resolveStorageLimitBytes(limitGb);
  if (limitBytes === null) return { allowed: true };

  if (usedBytes + pendingBytes + incomingBytes <= limitBytes) {
    return { allowed: true };
  }
  return { allowed: false, limitBytes };
};

export type FormatStorageLimitMessageInput = {
  used: number;
  limit: number;
  incoming: number;
  fileCount: number;
};

/**
 * Mirrors the wording of the API's 413 response so the client pre-check and a
 * server rejection read the same; only the trailing clause differs for batches.
 */
export const formatStorageLimitMessage = ({
  used,
  limit,
  incoming,
  fileCount,
}: FormatStorageLimitMessageInput): string => {
  const incomingClause =
    fileCount === 1
      ? `this file is ${convertBytesToSize(incoming)}`
      : `these ${fileCount} files total ${convertBytesToSize(incoming)}`;

  return `Storage limit exceeded. Current usage is ${convertBytesToSize(
    used
  )} of ${convertBytesToSize(limit)}; ${incomingClause}.`;
};
